import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * LocalCombobox — reusable bounded-height combobox.
 *
 * Same UX as the school search dropdown: typed-in filter, bounded panel
 * (`max-h-64`) with internal scroll, focus opens the panel, click-outside
 * closes it. Filtering happens client-side and is diacritics + case
 * insensitive so the same input matches "Cluj", "CLUJ", and "cluj".
 *
 * Used by:
 *   - <SchoolSelector /> for county + UAT
 *   - <TeacherProfileFields /> for specialization
 *
 * Designed for option lists that are small enough to load fully in JS
 * (≤500 items). For async / paginated search, use a different pattern.
 */
function _normalize(s) {
  if (!s) return "";
  return s
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export default function LocalCombobox({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  required = false,
  testid,
  fallbackPlaceholder,
}) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Keep the visible input synced when the parent's value changes (e.g.
  // resetting a dependent field).
  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const nq = _normalize(query);
    if (!nq || _normalize(value) === nq) return options;
    return options.filter((o) => _normalize(o).includes(nq));
  }, [query, options, value]);

  const cls =
    "w-full rounded-lg border border-[#E5E7EB] bg-[#F5F6F6] px-4 py-3 text-sm text-[#373A45] " +
    "outline-none focus:bg-white focus:border-[#965EA7] transition-colors " +
    "disabled:opacity-60 disabled:cursor-not-allowed";

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <input
          type="text"
          className={cls + " pr-9"}
          placeholder={disabled ? (fallbackPlaceholder || placeholder) : placeholder}
          value={query}
          disabled={disabled}
          onFocus={() => !disabled && setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            // Free-typing clears any previously locked-in value so dependent
            // fields reset cleanly.
            if (value) onChange("");
          }}
          required={required}
          data-testid={testid}
          autoComplete="off"
        />
        <ChevronDown
          size={16}
          className={
            "absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] transition-transform pointer-events-none " +
            (open ? "rotate-180" : "")
          }
        />
      </div>

      {open && !disabled && (
        <div
          className="absolute z-30 mt-1 w-full rounded-lg bg-white shadow-lg border border-[#E5E7EB] max-h-64 overflow-auto"
          data-testid={`${testid}-results`}
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-[#6B7280]">Niciun rezultat.</div>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange(opt);
                  setQuery(opt);
                  setOpen(false);
                }}
                className={
                  "w-full text-left px-4 py-2 hover:bg-[#F5F6F6] focus:bg-[#EDEEF0] focus:outline-none text-sm " +
                  (_normalize(opt) === _normalize(value)
                    ? "bg-[#F3E8F7] text-[#965EA7] font-medium"
                    : "text-[#373A45]")
                }
              >
                {opt}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
